'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface PartnershipType {
  id: string;
  title: string;
  description: string;
  icon: string;
  benefits: string[];
}

interface PartnershipInquiryProps {
  onInquiryClick: (partnershipType: string) => void;
}

const PartnershipInquiry: React.FC<PartnershipInquiryProps> = ({ onInquiryClick }) => {
  const partnershipTypes: PartnershipType[] = [
    {
      id: 'healthcare',
      title: 'Healthcare Provider Partnership',
      description: 'Collaborate with us to extend affordable healthcare services to underserved communities.',
      icon: 'HeartIcon',
      benefits: [
        'Expand your social impact reach',
        'Access to community network',
        'Joint health camp initiatives',
        'Professional recognition',
      ],
    },
    {
      id: 'corporate',
      title: 'Corporate CSR Partnership',
      description: 'Align your CSR initiatives with our community development programs for measurable impact.',
      icon: 'BuildingOfficeIcon',
      benefits: [
        'Customized CSR programs',
        'Transparent impact reporting',
        'Employee engagement opportunities',
        'Tax benefits under CSR compliance',
      ],
    },
    {
      id: 'educational',
      title: 'Educational Institution Partnership',
      description: 'Partner with us to provide skill development and educational support to underprivileged youth.',
      icon: 'AcademicCapIcon',
      benefits: [
        'Student volunteer programs',
        'Community service credits',
        'Research collaboration opportunities',
        'Social impact projects',
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center max-w-3xl mx-auto mb-8">
        <h3 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-3">
          Partnership Opportunities
        </h3>
        <p className="text-muted-foreground">
          Join hands with us to create lasting social impact. Together, we can build stronger, healthier communities.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {partnershipTypes.map((partnership) => (
          <div
            key={partnership.id}
            className="bg-card rounded-xl shadow-elevated p-6 hover:shadow-cta transition-all duration-300 group"
          >
            <div className="w-16 h-16 bg-supporting/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
              <Icon name={partnership.icon as any} size={32} className="text-supporting" variant="solid" />
            </div>

            <h4 className="text-xl font-heading font-bold text-foreground mb-3">
              {partnership.title}
            </h4>

            <p className="text-sm text-muted-foreground mb-4">
              {partnership.description}
            </p>

            <div className="space-y-2 mb-6">
              <p className="text-sm font-semibold text-foreground">Key Benefits:</p>
              <ul className="space-y-2">
                {partnership.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
                    <Icon name="CheckCircleIcon" size={16} className="text-secondary mt-0.5 flex-shrink-0" variant="solid" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => onInquiryClick(partnership.id)}
              className="w-full bg-supporting text-supporting-foreground font-heading font-semibold py-3 rounded-lg hover:bg-supporting/90 transition-all duration-300 shadow-subtle hover:shadow-cta flex items-center justify-center space-x-2"
            >
              <span>Inquire Now</span>
              <Icon name="ArrowRightIcon" size={18} />
            </button>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-supporting/10 rounded-xl p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex-1">
            <h4 className="text-xl font-heading font-bold text-foreground mb-2">
              Custom Partnership Solutions
            </h4>
            <p className="text-muted-foreground">
              Have a unique partnership idea? We're open to exploring innovative collaboration models that align with our mission.
            </p>
          </div>
          <button
            onClick={() => onInquiryClick('custom')}
            className="bg-primary text-primary-foreground font-heading font-semibold px-8 py-3 rounded-lg hover:bg-primary/90 transition-all duration-300 shadow-subtle hover:shadow-cta whitespace-nowrap"
          >
            Discuss Custom Partnership
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartnershipInquiry;